import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { MdOutlineGroup, MdOutlineWarningAmber } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { EMAIL } from "../../config/apiConfig";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { Badge } from "./Primitives";
import { formatNumber } from "./crmHelpers";

/*
  Montador de segmento.

  Não inventa uma linguagem de consulta: os campos são os MESMOS filtros da
  tela de Clientes, e a contagem vem do mesmo serviço que gera aquela tabela.
  É o que faz "7 contas" na tela de clientes e "7 destinatários" na campanha
  nunca divergirem.

  A contagem ao vivo é o ponto central da tela. Montar segmento às cegas e
  descobrir o tamanho só depois de disparar é como se erra feio em e-mail em
  massa — e o número vem decomposto (quantos sem e-mail, quantos
  descadastrados, quantos duplicados) porque a diferença entre "412 contas" e
  "389 destinatários" precisa ter explicação na hora, não virar desconfiança
  depois.
*/

const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {label}
    </label>
    {children}
    {hint ? <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p> : null}
  </div>
);

const inputClass =
  "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100";

const SegmentBuilder = ({ segment, onChange, disabled = false }) => {
  const [localCount, setLocalCount] = useState(null);
  const debounced = useDebouncedValue(JSON.stringify(segment), 500);

  const { data: options } = useQuery(["email", "segment-options"], async () => {
    const { data } = await axios.get(EMAIL.SEGMENT_OPTIONS);
    return data.data;
  });

  const { data: stats, isFetching } = useQuery(
    ["email", "segment-count", debounced],
    async () => {
      const { data } = await axios.post(EMAIL.SEGMENT_COUNT, JSON.parse(debounced));
      return data.data;
    },
    { keepPreviousData: true }
  );

  useEffect(() => {
    if (stats) setLocalCount(stats);
  }, [stats]);

  const set = (patch) => onChange({ ...segment, ...patch });

  return (
    <div className="space-y-4">
      <div
        className={`flex items-center gap-3 rounded-2xl border p-4 ${
          localCount?.deliverable
            ? "border-emerald-200 bg-emerald-50/60"
            : "border-amber-200 bg-amber-50/60"
        }`}
      >
        <span className="rounded-xl bg-white/70 p-2 text-slate-500">
          {localCount?.deliverable ? (
            <MdOutlineGroup size={22} />
          ) : (
            <MdOutlineWarningAmber size={22} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-bold leading-none text-slate-900">
            {isFetching && !localCount
              ? "…"
              : formatNumber(localCount?.deliverable || 0)}
            <span className="ml-2 text-sm font-medium text-slate-500">
              destinatário(s)
            </span>
          </p>
          {localCount ? (
            <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-slate-600">
              <span>{formatNumber(localCount.matched)} contas no segmento</span>
              {localCount.withoutEmail > 0 ? (
                <span className="text-amber-700">
                  {formatNumber(localCount.withoutEmail)} sem e-mail
                </span>
              ) : null}
              {localCount.suppressed > 0 ? (
                <span className="text-rose-700">
                  {formatNumber(localCount.suppressed)} descadastrada(s)
                </span>
              ) : null}
              {localCount.duplicated > 0 ? (
                <span>
                  {formatNumber(localCount.duplicated)} e-mail repetido
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Estágio no ciclo de vida">
          <select
            disabled={disabled}
            className={inputClass}
            value={segment.stage || ""}
            onChange={(event) => set({ stage: event.target.value })}
          >
            <option value="">Todos os estágios</option>
            {(options?.stages || []).map((stage) => (
              <option key={stage.id} value={stage.id} title={stage.description}>
                {stage.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Plano">
          <select
            disabled={disabled}
            className={inputClass}
            value={segment.planId || ""}
            onChange={(event) => set({ planId: event.target.value })}
          >
            <option value="">Todos os planos</option>
            {(options?.plans || []).map((plan) => (
              <option key={plan} value={plan}>
                {plan}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Canal de aquisição">
          <select
            disabled={disabled}
            className={inputClass}
            value={segment.acquisitionSource || ""}
            onChange={(event) => set({ acquisitionSource: event.target.value })}
          >
            <option value="">Todos os canais</option>
            {(options?.sources || []).map((source) => (
              <option key={source.value} value={source.value}>
                {source.label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Agendamentos em 30 dias"
          hint="Deixe em branco para não limitar"
        >
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              disabled={disabled}
              placeholder="mín."
              className={inputClass}
              value={segment.minAppointments30d ?? ""}
              onChange={(event) =>
                set({ minAppointments30d: event.target.value })
              }
            />
            <input
              type="number"
              min="0"
              disabled={disabled}
              placeholder="máx."
              className={inputClass}
              value={segment.maxAppointments30d ?? ""}
              onChange={(event) =>
                set({ maxAppointments30d: event.target.value })
              }
            />
          </div>
        </Field>

        <Field label="Dias desde o cadastro">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              disabled={disabled}
              placeholder="mín."
              className={inputClass}
              value={segment.minDaysSinceRegistration ?? ""}
              onChange={(event) =>
                set({ minDaysSinceRegistration: event.target.value })
              }
            />
            <input
              type="number"
              min="0"
              disabled={disabled}
              placeholder="máx."
              className={inputClass}
              value={segment.maxDaysSinceRegistration ?? ""}
              onChange={(event) =>
                set({ maxDaysSinceRegistration: event.target.value })
              }
            />
          </div>
        </Field>

        <Field label="Busca livre" hint="Nome, e-mail, telefone, documento, #id">
          <input
            disabled={disabled}
            className={inputClass}
            value={segment.search || ""}
            onChange={(event) => set({ search: event.target.value })}
          />
        </Field>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          disabled={disabled}
          className="h-4 w-4 rounded accent-pink-600"
          checked={Boolean(segment.neverContacted)}
          onChange={(event) => set({ neverContacted: event.target.checked })}
        />
        Só contas que a equipe nunca contatou
      </label>

      {localCount?.deliverable > 500 ? (
        /*
          Aviso de ritmo, não de erro. Com o teto padrão de 180/hora, uma
          campanha grande leva horas — e quem não sabe disso acha que travou e
          dispara de novo.
        */
        <p className="rounded-xl bg-sky-50 px-3 py-2 text-xs text-sky-900 ring-1 ring-inset ring-sky-200">
          <Badge tone="info">atenção ao ritmo</Badge>{" "}
          {formatNumber(localCount.deliverable)} e-mails saem aos poucos para
          não derrubar a entrega do domínio — leve algumas horas. Acompanhe pelo
          relatório da campanha.
        </p>
      ) : null}
    </div>
  );
};

export default SegmentBuilder;
